import React, { Component, ReactNode } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Error caught by boundary:', error, errorInfo);
        // TODO: Send to error tracking service (Sentry, etc.)
    }

    handleReset = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <Container className="mt-5">
                    <Alert variant="danger">
                        <Alert.Heading>Något gick fel</Alert.Heading>
                        <p>
                            Ett oväntat fel uppstod. Vänligen ladda om sidan eller kontakta support om problemet kvarstår.
                        </p>
                        {this.state.error && (
                            <details className="mt-3">
                                <summary style={{ cursor: 'pointer' }}>Teknisk information</summary>
                                <pre className="mt-2 p-3 bg-light border rounded" style={{ fontSize: '0.875rem' }}>
                                    {this.state.error.message}
                                    {'\n\n'}
                                    {this.state.error.stack}
                                </pre>
                            </details>
                        )}
                        <hr />
                        <div className="d-flex gap-2">
                            <Button variant="primary" onClick={() => window.location.reload()}>
                                Ladda om sidan
                            </Button>
                            <Button variant="outline-secondary" onClick={this.handleReset}>
                                Försök igen
                            </Button>
                        </div>
                    </Alert>
                </Container>
            );
        }

        return this.props.children;
    }
}
